import { useEffect } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { beiAenderung } from '../lib/aktualisierung'
import { useAuth, useIch } from '../lib/auth'
import { anzeigename } from '../lib/menschen'
import { useLaden } from '../lib/laden'
import { holeUebersicht, type Schlange } from '../lib/warteschlangen'

/**
 * Schmale Seitenleiste links mit der Anzahl der wartenden Vorgänge, rechts
 * der Inhalt (§10).
 */

const SCHLANGEN: { pfad: string; name: string; schluessel?: Schlange['name'] }[] = [
  { pfad: '/', name: 'Übersicht' },
  { pfad: '/fotos', name: 'Fotos', schluessel: 'fotos' },
  { pfad: '/bewertungen', name: 'Bewertungen', schluessel: 'bewertungen' },
  { pfad: '/einreichungen', name: 'Einreichungen', schluessel: 'einreichungen' },
  { pfad: '/meldungen', name: 'Meldungen', schluessel: 'meldungen' },
  {
    pfad: '/profilmeldungen',
    name: 'Profilmeldungen',
    schluessel: 'profilmeldungen',
  },
]

/**
 * Pflegearbeit: keine Warteschlange, aber auch keine Leseansicht. Sie steht
 * zwischen beidem, weil sie schreibt, ohne dass etwas fällig wird — 504 Orte
 * mit Bildern, die nebenher über Wochen durchgesehen werden.
 */
const PFLEGE = [{ pfad: '/ortsdurchsicht', name: 'Ortsdurchsicht' }]

/**
 * Die Auswertungen stehen abgesetzt darunter — sie sind reine Leseansichten
 * und dürfen die Arbeit nicht verdrängen: Die vier Warteschlangen sind, wofür
 * dieses Werkzeug gebaut wurde.
 */
const AUSWERTUNGEN = [
  { pfad: '/kennzahlen/kanaele', name: 'Kanäle' },
  { pfad: '/kennzahlen/pruefstand', name: 'Prüfstand' },
  { pfad: '/kennzahlen/journal', name: 'Journal' },
  { pfad: '/kennzahlen/dubletten', name: 'Dubletten' },
  { pfad: '/kennzahlen/herkunft', name: 'Herkunft' },
]

export default function Rahmen() {
  const ich = useIch()
  const { abmelden } = useAuth()
  const ort = useLocation()
  const [zustand, zahlenNeuLaden] = useLaden(holeUebersicht, [ort.pathname])

  // Nach jeder Entscheidung stimmen die Zahlen wieder.
  useEffect(() => beiAenderung(zahlenNeuLaden), [zahlenNeuLaden])

  const zahlen = new Map<string, number>(
    zustand.art === 'da' ? zustand.wert.schlangen.map((q) => [q.name, q.anzahl]) : [],
  )

  return (
    <div className="rahmen">
      <nav className="seitenleiste" aria-label="Warteschlangen">
        <p className="marke seitenleiste-marke">Kinderspiel</p>
        <ul className="seitenleiste-liste">
          {SCHLANGEN.map((s) => {
            const anzahl = s.schluessel ? zahlen.get(s.schluessel) : undefined
            return (
              <li key={s.pfad}>
                <NavLink
                  to={s.pfad}
                  end={s.pfad === '/'}
                  className={({ isActive }) =>
                    isActive ? 'seitenleiste-glied aktiv' : 'seitenleiste-glied'
                  }
                >
                  <span>{s.name}</span>
                  {anzahl !== undefined && anzahl > 0 && (
                    <span className="wartend zahl" aria-label={`${anzahl} wartend`}>
                      {anzahl}
                    </span>
                  )}
                </NavLink>
              </li>
            )
          })}
        </ul>

        <div>
          <p className="seitenleiste-gruppe">Pflege</p>
          <ul className="seitenleiste-liste">
            {PFLEGE.map((a) => (
              <li key={a.pfad}>
                <NavLink
                  to={a.pfad}
                  className={({ isActive }) =>
                    isActive ? 'seitenleiste-glied aktiv' : 'seitenleiste-glied'
                  }
                >
                  <span>{a.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="seitenleiste-gruppe">Auswertung</p>
          <ul className="seitenleiste-liste">
            {AUSWERTUNGEN.map((a) => (
              <li key={a.pfad}>
                <NavLink
                  to={a.pfad}
                  className={({ isActive }) =>
                    isActive ? 'seitenleiste-glied aktiv' : 'seitenleiste-glied'
                  }
                >
                  <span>{a.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <div className="inhalt">
        <header className="kopf">
          <div className="kopf-mensch">
            <span>{anzeigename(ich.profil, ich.uid)}</span>
            <span className="leise kopf-rolle">
              {ich.rolle === 'admin' ? 'Admin' : 'Moderation'}
            </span>
          </div>
          <button
            className="knopf knopf-still"
            type="button"
            onClick={() => void abmelden()}
          >
            Abmelden
          </button>
        </header>

        <div className="inhalt-koerper">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
